/**
 * UNIFIED-SHELL-AND-ROLE-NAVIGATION-V1 (V7-P4 Slice 2) -- the ONE workflow
 * handoff grammar.
 *
 * The "workflow handoff" experience rule: the product surfaces the NEXT lawful
 * step so a screen never dead-ends (issue #285). This module NAMES the three
 * demo-path, status-driven handoffs as ONE grammar -- a `{ step, label, to }`
 * navigation descriptor, or `null` when the row's status does not warrant a
 * handoff:
 *
 *   check-in completed  -> Doctor queue / consult      (doctor lens)
 *   consult completed   -> Checkout / collect payment  (front-desk lens)
 *   payment recorded    -> Follow-up                   (front-desk lens; deferred)
 *
 * NAVIGATION ONLY. It writes nothing, advances no state machine, and invents no
 * field: it COMPOSES the EXISTING pure derivations already shipped on the day
 * board -- `consultDoorForRow` / `consultPathForAppointment` (the doctor leg)
 * and the finalized-visit / saved-positive-balance gate with `collectCtaLabel`
 * (the checkout leg). The wired
 * UI instances of this grammar are the ClinicDayBoard doors (the doctor consult
 * door; the receptionist Collect / Record-payment door); this module is the
 * grammar those doors follow, pinned by `workflow-handoff.test.ts`.
 *
 * STATUS-DRIVEN + WRONG-STATE -> NO HANDOFF: a booked (not-checked-in) row
 * offers no consult door; a mid-consult row offers no checkout; an
 * estimate-only / zero-or-unknown-balance / cancelled / no-show row offers no collect
 * step. Every destination is one the acting role ALREADY reaches lawfully --
 * a handoff never widens authority (the route guards + SELECT-level fences
 * remain the boundary).
 */

import type { TodayAppointment } from '@/dashboard/types';
import {
  consultDoorForRow,
  consultPathForAppointment,
} from '@/dashboard/doctor/doctor-home-view';
import {
  collectCtaLabel,
  derivePaymentHandoff,
} from '@/dashboard/appointments/payment-handoff';

export type WorkflowStep = 'to-doctor' | 'to-checkout' | 'to-followup';

export interface WorkflowHandoff {
  readonly step: WorkflowStep;
  readonly label: string;
  /** The lawful destination the acting role already reaches. */
  readonly to: string;
  /** Presentation weight for the affordance (mirrors the day-board doors). */
  readonly emphasis: 'primary' | 'ghost';
}

// --- Status gates (pure predicates; the "wrong state -> no handoff" boundary) ---

/** Check-in completed: the visit has a consult door (checked in / in consult /
 *  finalized). A booked / confirmed / arrived / cancelled row has none. */
export function checkInComplete(row: TodayAppointment): boolean {
  return consultDoorForRow(row) !== null;
}

/** Completed consult and a saved positive visit balance permit the Home handoff. */
export function checkoutDue(row: TodayAppointment): boolean {
  return checkoutHandoff(row) !== null;
}

/** Payment recorded: the gate for the follow-up transition. */
export function paymentRecorded(row: TodayAppointment): boolean {
  return derivePaymentHandoff(row).kind === 'recorded';
}

// --- The handoffs (navigation descriptors; null = no lawful handoff yet) ---

/** check-in completed -> Doctor queue / consult (doctor lens). */
export function doctorConsultHandoff(
  row: TodayAppointment,
): WorkflowHandoff | null {
  const door = consultDoorForRow(row);
  if (door === null) return null;
  return {
    step: 'to-doctor',
    label: door.label,
    to: consultPathForAppointment(row.id),
    emphasis: door.emphasis,
  };
}

/** Completed consult with verified visit balance -> collect payment (front-desk lens). The
 *  destination is the billing-entry surface keyed by the appointment; the
 *  surface adds its own prefill context (patient / clinic / suggested sum),
 *  so this base descriptor stays generic. */
export function checkoutHandoff(
  row: TodayAppointment,
): WorkflowHandoff | null {
  // Ravi's 9 October Package 1 preserve rule: no Reception Home collection
  // while waiting or in consult. A saved balance alone does not close care;
  // treatment estimates and previous payments never establish the amount due.
  const due = row.outstandingMinorUnits;
  if (row.visitStatus !== 'finalized' || typeof due !== 'number' || !Number.isSafeInteger(due) || due <= 0 ||
      ['cancelled', 'no_show'].includes(row.status)) return null;
  return {
    step: 'to-checkout',
    label: collectCtaLabel(due % 100 === 0 ? due / 100 : null),
    to: `/dashboard/billing-entry?appointmentId=${row.id}`,
    emphasis: 'primary',
  };
}

/**
 * payment recorded -> Follow-up (front-desk lens). RECOGNIZED but DEFERRED:
 * a dedicated follow-up scheduling ROUTE is not built yet (the receptionist
 * Today home carries an honest not-yet-configured Follow-ups panel), so this
 * status-driven transition has no lawful destination to navigate to today and
 * returns `null` -- never a fabricated dead link. The `paymentRecorded` gate
 * above is exported so the follow-up surface can wire this leg when it lands.
 */
export function followupHandoff(_row: TodayAppointment): WorkflowHandoff | null {
  return null;
}

/**
 * The single lawful next navigation step for an acting role + row, or `null`
 * when the status does not warrant one. Read-only / supervisory roles get no
 * actionable handoff from this grammar (they read the board; SEE-NOT-DO).
 */
export function nextWorkflowStep(
  row: TodayAppointment,
  role: string | null,
): WorkflowHandoff | null {
  if (role === 'doctor') return doctorConsultHandoff(row);
  if (role === 'receptionist') {
    return checkoutHandoff(row) ?? followupHandoff(row);
  }
  return null;
}

// --- HANDOFF LINES (V7-P4 continued adoption of the merged slice-2/3
// grammar; #285 zero-dead-ends) ---------------------------------------------
//
// At every COMPLETION MOMENT on the demo chain the user is told WHO acts
// next and WHERE (the five-second / 30-second stranger-scan wording bar,
// Benchmark Book A rows A4/A7). These are STATUS LINES, not navigation:
// where the next step belongs to ANOTHER role, the line names that role
// honestly instead of offering a door the viewer does not hold (LES-016).
// The board wire carries NO doctor display name (TodayAppointment has no
// such field), so the queue is named generically -- naming the doctor would
// need an API change this package is fenced from.

/** After the check-in hop lands: the next actor is the DOCTOR, from their
 *  own Today queue (spoken by the desk's transient success feedback). */
export const CHECKED_IN_HANDOFF_LINE =
  "Now in the doctor's queue - the consult starts from the doctor's Today.";

/** The desk feedback for a successful check-in: the existing success copy
 *  plus the who-acts-next line (composed, never replacing the proven copy). */
export function checkedInSuccessFeedback(successCopy: string): string {
  return `${successCopy} ${CHECKED_IN_HANDOFF_LINE}`;
}

/** After registration is saved (plain success card, no booking yet): the
 *  next act belongs to the FRONT DESK itself, on the day board. */
export const REGISTRATION_SAVED_NEXT_ACTION_LABEL =
  "Check the visitor in from Today's day board on arrival - check-in hands them to the doctor's queue";

/** While a consult is OPEN (started, not closed): the doctor's one next act. */
export const CONSULT_OPEN_NEXT_ACTION_LABEL =
  'Close the visit when the consult ends - closing hands the patient to the front desk for checkout';

/** After clinical close: Money, not the treatment estimate, decides settlement. */
export const CONSULT_CLOSED_PAID_LINE =
  'Reception checks the saved bill balance. Settled visits move to Finished; visits with money due stay on Home for Collect payment.';

/** Compatibility export for existing consumers of the same truthful handoff. */
export const CONSULT_CLOSED_NOTHING_DUE_LINE =
  CONSULT_CLOSED_PAID_LINE;

/** After a payment is recorded (billing receipt): the follow-up leg. A
 *  scheduling ROUTE does not exist yet (followupHandoff above), so the line
 *  names the REAL pickup surfaces the front desk and COC already hold. */
export const PAYMENT_RECORDED_NEXT_ACTION_LABEL =
  'Follow-up is next - the front desk and COC pick it up from Open Follow-ups when one is due';
